import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { FadeUp, Eyebrow } from "@/components/Motion";
import ProjectCard from "@/components/ProjectCard";

const COPY = {
  all: {
    eyebrow: "All Work",
    title: "Everything we've made, in one place.",
    blurb:
      "Original films alongside service production for agencies, labels, and streamers.",
  },
  original: {
    eyebrow: "Original Work",
    title: "Original stories, built with filmmakers.",
    blurb:
      "Features, documentaries, and shorts developed with distinct voices and points of view — from early development through production and release.",
  },
  service: {
    eyebrow: "Service Production in Toronto",
    title: "Production support built around your shoot.",
    blurb:
      "Crew, equipment, locations and production support across Toronto and Ontario.",
  },
};

const getProjectStatus = (project) => {
  if (project.status) return project.status;
  return project.in_development ? "In Development" : "Released";
};

export default function Work({ category = "all" }) {
  const preloaded =
    typeof window !== "undefined" &&
    window.__INCOLOUR_PRERENDER__?.path === window.location.pathname
      ? window.__INCOLOUR_PRERENDER__
      : null;

  const [projects, setProjects] = useState(() => preloaded?.projects || []);
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(() => !preloaded);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!preloaded) {
      setLoading(true);
    }

    setError(false);
    setTypeFilter("all");

    const q =
      category === "all" ? "/projects" : `/projects?category=${category}`;

    api
      .get(q)
      .then((r) => {
        setProjects(r.data);
        setError(false);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [category]);

  const types = useMemo(
    () => [
      "all",
      ...new Set(
        projects.filter((p) => !p.in_development).map((p) => p.type)
      ),
    ],
    [projects]
  );

  const devProjects = projects.filter((p) => p.in_development);
  const released = projects.filter((p) => !p.in_development);

  const filtered =
    typeFilter === "all"
      ? released
      : released.filter((p) => p.type === typeFilter);

  const c = COPY[category];

  const trackPartnerClick = (partner) => {
    if (!window.gtag) return;

    window.gtag("event", "production_partner_click", {
      partner,
      source: "service_production_network",
    });
  };

  return (
    <div className="pt-16" data-testid={`work-page-${category}`}>
      <SEO
        title={
          category === "service"
            ? "Toronto Service Production for Film, TV & Commercials"
            : category === "original"
            ? "Original Films"
            : "Film & Production Portfolio"
        }
        description={
          category === "service"
            ? "Incolour Films provides full-service production in Toronto and Ontario for film, television, commercials and branded content, including crew, locations, equipment, casting and production support."
            : category === "original"
            ? "Explore original films developed and produced by Incolour Films with filmmakers from diverse backgrounds, cultures and perspectives."
            : "Explore original films and service production work produced by Incolour Films in Toronto."
        }
        path={category === "all" ? "/work" : `/${category}`}
        noindex={category === "all"}
        jsonLd={
          category === "service"
            ? {
                "@context": "https://schema.org",
                "@type": "Service",
                name: "Film, TV & Commercial Production Services",
                serviceType: "Film and television production services",
                url: "https://incolourfilms.ca/service",
                provider: {
                  "@type": "Organization",
                  name: "Incolour Films",
                  url: "https://incolourfilms.ca",
                },
                areaServed: [
                  {
                    "@type": "City",
                    name: "Toronto",
                  },
                  {
                    "@type": "AdministrativeArea",
                    name: "Ontario",
                  },
                  {
                    "@type": "Country",
                    name: "Canada",
                  },
                ],
              }
            : null
        }
      />

      <section className="mx-auto max-w-7xl px-4 pb-8 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <Eyebrow>{c.eyebrow}</Eyebrow>

        <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[1.0] tracking-tight sm:text-6xl">
          {c.title}
        </h1>

        <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          {c.blurb}
        </p>
      </section>

      {category === "original" && devProjects.length > 0 && (
        <section
          className="border-y border-border bg-card"
          data-testid="in-development-section"
        >
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-18">
            <Eyebrow>Current Slate</Eyebrow>

            <div className="mt-4 grid gap-6 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-7">
                <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                  In development.
                </h2>

                <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
                  Projects we&apos;re actively developing with filmmakers —
                  stories at different stages of writing, packaging, financing,
                  and production.
                </p>
              </div>

              <div className="lg:col-span-5 lg:text-right">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 text-sm font-semibold"
                  data-testid="development-contact-link"
                >
                  Have a story to bring us?
                  <ArrowUpRight
                    size={16}
                    className="text-orange transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </Link>
              </div>
            </div>

            <div className="mt-10 divide-y divide-border border-y border-border">
              {devProjects.map((p) => (
                <FadeUp key={p.id}>
                  <Link
                    to={`/projects/${p.slug}`}
                    className="group grid gap-4 py-7 transition-colors hover:text-orange sm:grid-cols-[1fr_auto] sm:items-center"
                    data-testid={`dev-project-${p.slug}`}
                  >
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="font-display text-xl font-bold tracking-tight sm:text-2xl">
                          {p.title}
                        </h3>

                        <span className="rounded-full border border-orange/40 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.16em] text-orange">
                          {getProjectStatus(p)}
                        </span>
                      </div>

                      <p className="mt-2 text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                        {p.type}
                        {p.year ? ` · ${p.year}` : ""}
                      </p>
                    </div>

                    <ArrowUpRight
                      size={18}
                      className="text-orange transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    />
                  </Link>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        {category === "original" && (
          <div className="mb-8">
            <Eyebrow>Released Work</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Films we&apos;ve brought to screen.
            </h2>
          </div>
        )}

        <div
          className="flex flex-wrap gap-2"
          role="tablist"
          aria-label="Filter by format"
        >
          {types.map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={typeFilter === t}
              onClick={() => setTypeFilter(t)}
              data-testid={`type-filter-${t
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
              className={`rounded-full border px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] transition-colors ${
                typeFilter === t
                  ? "border-ink bg-ink text-cream dark:border-cream dark:bg-cream dark:text-ink"
                  : "border-border text-muted-foreground hover:border-ink hover:text-foreground dark:hover:border-cream"
              }`}
            >
              {t === "all" ? `All (${released.length})` : t}
            </button>
          ))}
        </div>

        {loading ? (
          <p
            className="mt-14 text-muted-foreground"
            data-testid="work-loading"
          >
            Loading work…
          </p>
        ) : error ? (
          <p
            className="mt-14 text-muted-foreground"
            data-testid="work-error"
          >
            We’re having trouble loading this content right now. Please refresh
            in a moment.
          </p>
        ) : filtered.length === 0 ? (
          <p
            className="mt-14 text-muted-foreground"
            data-testid="work-empty"
          >
            Nothing here yet — check back soon.
          </p>
        ) : (
          <div
            className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8"
            data-testid="work-grid"
          >
            {filtered.map((p, i) => (
              <ProjectCard key={p.id} p={p} i={i} />
            ))}
          </div>
        )}
      </section>

      {category === "service" && (
        <section
          className="border-t border-border bg-card py-16 lg:py-20"
          data-testid="international-productions-section"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Eyebrow>International Productions</Eyebrow>

            <div className="mt-4 grid gap-8 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-7">
                <h2 className="max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                  Bringing a production to Canada?
                </h2>

                <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                  Keep your key creative team. We help build the production
                  around them here.
                </p>
              </div>

              <div className="lg:col-span-5 lg:text-right">
                <Link
                  to="/contact?type=service"
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag("event", "service_inquiry_click", {
                        source: "service_international_section",
                        page_category: "service",
                      });
                    }
                  }}
                  className="group inline-flex items-center gap-2 text-sm font-semibold"
                  data-testid="international-production-cta"
                >
                  Plan a Canadian shoot
                  <ArrowUpRight
                    size={16}
                    className="text-orange transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </Link>
              </div>
            </div>

            <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  title: "Local Crew",
                  body: "Build the Canadian crew around your travelling team.",
                },
                {
                  title: "Equipment & Vendors",
                  body: "Camera, lighting, grip, studios and production resources.",
                },
                {
                  title: "Locations & Logistics",
                  body: "Locations, transport, scheduling and local coordination.",
                },
                {
                  title: "Budget & Incentives",
                  body: "Canadian budgeting and production planning with tax credits in mind.",
                },
              ].map((item) => (
                <div key={item.title} className="bg-background p-6 sm:p-7">
                  <h3 className="font-display text-xl font-bold tracking-tight">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {category === "service" && (
        <section className="border-t border-border py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Eyebrow>Production Network</Eyebrow>

            <div className="mt-4 grid gap-8 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                  The network is already in place.
                </h2>
              </div>

              <div className="lg:col-span-7">
                <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">
                  Trusted rental houses, studios, transport and production
                  vendors across Toronto and Ontario.
                </p>
              </div>
            </div>

            <p className="mt-12 text-xs font-mono uppercase tracking-[0.22em] text-muted-foreground">
              Production Partners
            </p>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <a
                href="https://ontariocamera.com/"
                target="_blank"
                onClick={() => trackPartnerClick("Ontario Camera")}
                rel="noopener noreferrer"
                className="group rounded-2xl border border-border p-6 transition-colors hover:border-orange"
              >
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  Camera &amp; Production Equipment
                </p>

                <h3 className="mt-3 font-display text-xl font-bold tracking-tight group-hover:text-orange">
                  Ontario Camera
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Camera, lighting and grip.
                </p>
              </a>

              <a
                href="https://mbscanada.ca/"
                target="_blank"
                onClick={() => trackPartnerClick("MBSE Canada")}
                rel="noopener noreferrer"
                className="group rounded-2xl border border-border p-6 transition-colors hover:border-orange"
              >
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  Lighting, Grip &amp; Studios
                </p>

                <h3 className="mt-3 font-display text-xl font-bold tracking-tight group-hover:text-orange">
                  MBSE Canada
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Lighting, grip and studio infrastructure.
                </p>
              </a>

              <a
                href="https://www.advantagecarrentals.com/"
                target="_blank"
                onClick={() => trackPartnerClick("Advantage Car & Truck Rentals")}
                rel="noopener noreferrer"
                className="group rounded-2xl border border-border p-6 transition-colors hover:border-orange"
              >
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  Transportation &amp; Production Vehicles
                </p>

                <h3 className="mt-3 font-display text-xl font-bold tracking-tight group-hover:text-orange">
                  Advantage Car &amp; Truck Rentals
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Cars, vans and production vehicles.
                </p>
              </a>

              <a
                href="https://hamiltonfilmstudios.com/"
                target="_blank"
                onClick={() => trackPartnerClick("Hamilton Film Studios")}
                rel="noopener noreferrer"
                className="group rounded-2xl border border-border p-6 transition-colors hover:border-orange"
              >
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  Locations Support &amp; Expendables
                </p>

                <h3 className="mt-3 font-display text-xl font-bold tracking-tight group-hover:text-orange">
                  Hamilton Film Studios
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Location support and production resources.
                </p>
              </a>

              <a
                href="https://www.otrlocations.com/"
                target="_blank"
                onClick={() => trackPartnerClick("On The Road")}
                rel="noopener noreferrer"
                className="group rounded-2xl border border-border p-6 transition-colors hover:border-orange"
              >
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  Location &amp; Production Equipment
                </p>

                <h3 className="mt-3 font-display text-xl font-bold tracking-tight group-hover:text-orange">
                  On The Road
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Location equipment and on-set support.
                </p>
              </a>

              <a
                href="https://locationa.ca/"
                target="_blank"
                onClick={() => trackPartnerClick("Location A")}
                rel="noopener noreferrer"
                className="group rounded-2xl border border-border p-6 transition-colors hover:border-orange"
              >
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  Studio Space
                </p>

                <h3 className="mt-3 font-display text-xl font-bold tracking-tight group-hover:text-orange">
                  Location A
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Flexible studio space in Toronto.
                </p>
              </a>
            </div>
          </div>
        </section>
      )}

      {category === "service" && (
        <section className="border-t border-border py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Eyebrow>Why Toronto</Eyebrow>

            <div className="mt-4 grid gap-8 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                  A world class production base.
                </h2>
              </div>

              <div className="lg:col-span-7">
                <p className="max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                  Experienced crews, diverse locations, major studios and
                  established production infrastructure.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {category === "service" && (
        <section
          className="border-t border-border bg-cream2 py-16 dark:bg-card lg:py-20"
          data-testid="tax-incentives-section"
        >
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
            <div className="lg:col-span-5">
              <Eyebrow>Tax Incentives</Eyebrow>

              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Shoot in Canada.{" "}
                <span className="font-serif italic text-orange">
                  Make the incentives work for your production.
                </span>
              </h2>

              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
                International productions shooting in Ontario may qualify for
                federal and provincial production incentives. We help plan the
                local spend and production structure around those requirements.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2">
                {[
                  {
                    k: "16% Federal",
                    v: "Film or Video Production Services Tax Credit on eligible Canadian labour expenditures",
                  },
                  {
                    k: "21.5% Ontario",
                    v: "Ontario Production Services Tax Credit on qualifying Ontario production expenditures",
                  },
                  {
                    k: "Stackable incentives",
                    v: "Eligible productions may combine federal and Ontario production-services incentives, subject to program requirements",
                  },
                  {
                    k: "Production planning",
                    v: "Local budgeting, crew structures, vendor planning, and documentation built around your production requirements",
                  },
                ].map((t) => (
                  <FadeUp key={t.k}>
                    <div
                      className="h-full bg-background p-7"
                      data-testid={`tax-card-${t.k
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      <p className="font-display text-2xl font-extrabold tracking-tight text-orange">
                        {t.k}
                      </p>

                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {t.v}
                      </p>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <FadeUp className="border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 py-16 sm:px-6 lg:flex-row lg:items-center lg:px-8">
          <h2 className="max-w-3xl font-display text-2xl font-bold tracking-tight sm:text-3xl">
            {category === "original"
              ? "Have a story you want to develop with us?"
              : category === "service"
              ? "Bringing a film, TV or commercial production to Toronto?"
              : "Have a project in mind?"}
          </h2>

          <Link
            to={category === "service" ? "/contact?type=service" : "/contact"}
            onClick={() => {
              if (!window.gtag) return;

              const eventName =
                category === "service"
                  ? "service_inquiry_click"
                  : category === "original"
                  ? "bring_story_click"
                  : "project_inquiry_click";

              window.gtag("event", eventName, {
                source: `work_${category}_bottom_cta`,
                page_category: category,
              });
            }}
            data-testid="work-cta-link"
            className="rounded-full bg-orange px-7 py-3.5 text-sm font-semibold text-cream transition-transform hover:scale-[1.04]"
          >
            {category === "original" ? "Bring us your story" : "Talk to us"}
          </Link>
        </div>
      </FadeUp>
    </div>
  );
}